import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  DLG_BTN_PRIMARY,
  DLG_BTN_SECONDARY,
} from '@/components/ui/dialogButtons';

interface Props {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  initialData?: {
    id_lista_negra: string;
    telefono?: string;
    email?: string;
    motivo?: string;
    activo: boolean;
  };
  empresaId: string;
  userId: string;
  insertar: (
    idEmpresa: string,
    userId: string,
    data: {
      telefono?: string;
      email?: string;
      motivo?: string;
      activo: boolean;
    }
  ) => Promise<{ error: boolean }>;
  actualizar: (
    id: string,
    userId: string,
    data: {
      telefono?: string;
      email?: string;
      motivo?: string;
      activo: boolean;
    }
  ) => Promise<{ error: boolean }>;
}

export default function NewBlackListDialog({
  open,
  onClose,
  onSaved,
  initialData,
  empresaId,
  userId,
  insertar,
  actualizar,
}: Props) {
  const { t } = useTranslation();

  const [telefono, setTelefono] = useState('');
  const [email, setEmail] = useState('');
  const [motivo, setMotivo] = useState('');
  const [activo, setActivo] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (initialData) {
      setTelefono(initialData.telefono || '');
      setEmail(initialData.email || '');
      setMotivo(initialData.motivo || '');
      setActivo(initialData.activo);
    } else {
      setTelefono('');
      setEmail('');
      setMotivo('');
      setActivo(true);
    }
  }, [initialData, open]);

  const handleSave = async () => {
    if (submitting) return;
    setSubmitting(true);

    try {
      const payload = { telefono, email, motivo, activo };

      const result = initialData?.id_lista_negra
        ? await actualizar(initialData.id_lista_negra, userId, payload)
        : await insertar(empresaId, userId, payload);

      if (result?.error) return;

      onSaved();
      onClose();
    } catch {
      toast.error(t('newBlackListDialog.errorSave'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        className="
          z-[60] p-0 border border-gray-200 dark:border-white/10 bg-white dark:bg-[#1c1c1e]
          [&>button[type='button']]:hidden
          flex flex-col
          !bottom-0 !left-0 !top-auto !translate-x-0 !translate-y-0
          w-full max-w-none max-h-[90vh] rounded-t-2xl rounded-b-none
          data-[state=open]:slide-in-from-bottom data-[state=closed]:slide-out-to-bottom
          sm:!bottom-auto sm:!left-1/2 sm:!top-1/2 sm:!-translate-x-1/2 sm:!-translate-y-1/2
          sm:w-[95%] sm:max-w-lg sm:mx-auto sm:h-auto sm:max-h-[90vh] sm:rounded-xl sm:shadow-xl
        "
      >
        <div className="px-4 sm:px-6 pt-5 pb-3 shrink-0 relative">
          <DialogHeader>
            <DialogTitle className="text-lg sm:text-xl font-semibold text-[#103254] dark:text-blue-400 pr-10">
              {initialData
                ? t('newBlackListDialog.titleEdit')
                : t('newBlackListDialog.titleNew')}
            </DialogTitle>
          </DialogHeader>
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center rounded-full text-gray-500 hover:text-gray-800 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
            aria-label="Cerrar"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 sm:px-6 pb-5">
          <div className="rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 shadow-sm p-4 space-y-5 text-sm text-gray-700 dark:text-gray-300">
            <div className="space-y-2">
              <Label className="font-medium text-gray-700 dark:text-gray-300">
                {t('newBlackListDialog.labelPhone')}
              </Label>
              <Input
                type="tel"
                value={telefono}
                onChange={(e) => setTelefono(e.target.value)}
                placeholder={t('newBlackListDialog.phonePlaceholder')}
                className="rounded-md text-sm dark:bg-white/5 dark:border-white/10 dark:text-white"
              />
            </div>

            <div className="space-y-2">
              <Label className="font-medium text-gray-700 dark:text-gray-300">
                {t('newBlackListDialog.labelEmail')}
              </Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('newBlackListDialog.emailPlaceholder')}
                className="rounded-md text-sm dark:bg-white/5 dark:border-white/10 dark:text-white"
              />
            </div>

            <div className="space-y-2">
              <Label className="font-medium text-gray-700 dark:text-gray-300">
                {t('newBlackListDialog.labelReason')}
              </Label>
              <textarea
                value={motivo}
                onChange={(e) => setMotivo(e.target.value)}
                placeholder={t('newBlackListDialog.reasonPlaceholder')}
                className="w-full border border-gray-300 dark:border-white/10 bg-gray-50 dark:bg-white/5 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 rounded-lg p-3 text-sm resize-none focus:ring-2 focus:ring-[#103254] dark:focus:ring-blue-400 shadow-sm"
                rows={3}
              />
            </div>

            <div className="flex items-center gap-3">
              <Switch checked={activo} onCheckedChange={setActivo} />
              <span
                className={`text-sm font-medium ${
                  activo
                    ? 'text-green-600 dark:text-green-400'
                    : 'text-red-500 dark:text-red-400'
                }`}
              >
                {activo
                  ? t('newBlackListDialog.active')
                  : t('newBlackListDialog.inactive')}
              </span>
            </div>
          </div>
        </div>

        <DialogFooter className="px-4 sm:px-6 pt-2 pb-5 sm:pb-4 flex flex-col sm:flex-row gap-3 shrink-0 border-t border-gray-100 dark:border-white/10">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={submitting}
            className={`w-full sm:flex-1 ${DLG_BTN_SECONDARY}`}
          >
            {t('newBlackListDialog.cancel')}
          </Button>

          <Button
            onClick={handleSave}
            disabled={submitting}
            className={`w-full sm:flex-1 ${DLG_BTN_PRIMARY}`}
          >
            {submitting
              ? t('newBlackListDialog.saving')
              : initialData
              ? t('newBlackListDialog.saveEditBtn')
              : t('newBlackListDialog.saveBtn')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}